/* The nine sections of the visit review workspace.
   Visual reference: docs/design/visit-detail.html. Normative rules: docs/design/spec/.

   Each is a plain component over data the workspace already fetched — ONE section is mounted at a
   time, so nothing here may hold its own long-lived state beyond what the reader is looking at.

   Orders / returns / payments are read-only here: they carry a document status from Excalibur that
   this user does not own. Reports, task answers and price monitoring are the three moderatable
   kinds, and they are the ones the queue above hoists. */

import React, { useMemo, useState } from "react";
import { Alert as AntAlert, Button, Card, Empty, Flex, Image, Input, Segmented, Space, Table, Tag, Timeline, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, FileImageOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";

import { PersonCell, TableFoot, TableSteps } from "../../component/table-cells";
import LeafletMap from "../points/ext/LeafletMap";
import { PhotoReview } from "../../models/photo-review";
import { ENTITY_DROPDOWN_SETTINGS, POINTS_ADMIN, VISIT_PAGE } from "../../config/lang";
import { call } from "../../util/action";
import { verdictOf } from "../../util/answer-fields";
import { fill, translate } from "../../util/util";

import { distanceM, formatDistance, isMockPing, pingsOf } from "./gps";
import { RejectModal, SectionEmpty, StatusTag, dash, statusOf } from "./parts";

/* ------------------------------------------------------------------ helpers */

const rowsOf = response => (Array.isArray(response) ? response : response?.results || []);

export const fullName = user => (user ? [user.first_name, user.last_name].filter(Boolean).join(" ") : "");

/** Currency is not modelled anywhere in the app; the legacy tabs print a bare number too. */
export const money = value => (value === null || value === undefined || value === "" ? "" : Number(value).toFixed(2));

/* Order and Return statuses are stored as `TextChoices` KEYS, and DRF serialises the key, not the
   label — so the cell rendered `sent_for_delivering`, lowercase and English in all four locales.
   Bordered rather than filled: this is a document status from Excalibur that the supervisor does
   not own, so it must not read like one of our own `.status-tag` verdicts. The label carries the
   `-700` tone (it is text) while the border carries `-500` (spec/01#status-colour). */
const ORDER_STATUS = {
  new: "new",
  processing: "processing",
  sent_for_delivering: "ok",
  sent_to_logistics: "ok",
  closed: "closed",
  canceled: "no",
};

export function OrderStatusTag({ value, lang }) {
  if (!value) return dash;
  const tone = ORDER_STATUS[value] || "new";
  const label = translate(VISIT_PAGE, `order_status_${value}`, lang);
  return (
    <Tag bordered className={`vd-ostatus vd-ostatus--${tone}`}>
      {label || value}
    </Tag>
  );
}

/* Newest first. These endpoints return a per-visit list with no pagination and no ordering — and
   neither `Order` nor `Return` declares a `Meta.ordering`, so the rows arrive in whatever order the
   database felt like. Sorting the loaded list is therefore the whole list, not a page of it. */
const newestFirst = (rows, key = "datetime") => [...rows].sort((a, b) => new Date(b[key] || 0) - new Date(a[key] || 0));

/** `additional_info.tags` is an array, or a bare string when there is exactly one. */
const tagsOf = tags => (Array.isArray(tags) ? tags : typeof tags === "string" && tags ? [tags] : []);

/** A table that scrolls inside itself, so the page never scrolls sideways (spec/02). */
function SectionTable({ columns, rows, onRow, minWidth, rowSelection }) {
  return (
    <Table
      className="vd-table"
      size="small"
      rowKey="id"
      columns={columns}
      dataSource={rows}
      pagination={false}
      scroll={{ x: minWidth }}
      rowSelection={rowSelection}
      onRow={record => ({
        onClick: event => {
          /* The same guard every list page carries: a click on the checkbox or a button is not
             a request to open the record. */
          if (event.target.closest(".ant-table-selection-column, .ant-btn")) return;
          if (onRow) onRow(record);
        },
        className: onRow ? "vd-row" : "",
      })}
    />
  );
}

/**
 * A section's pager — the app's shared `TableFoot`, so the rows-per-page control, the range and the
 * page window read the same here as on every list page.
 *
 * `total` is the caller's, because only the caller knows whether it is the server's `count` or the
 * length of a list already in memory.
 */
function SectionFoot({ id, total, pager }) {
  if (!pager || total <= pager.size) return null;
  return (
    <TableFoot
      id={id}
      page={pager.page}
      size={pager.size}
      total={total}
      sizes={pager.sizes}
      labels={pager.labels}
      onPage={pager.onPage}
      onSize={pager.onSize}
    />
  );
}

/** The trailing chevron every drill-in row carries in the design. */
const openColumn = label => ({
  key: "open",
  width: 56,
  align: "right",
  render: () => <Button type="text" size="small" icon={<RightOutlined />} aria-label={label} />,
});

/* --------------------------------------------------------- orders / returns / payments */

export function OrdersSection({ data, lang, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <SectionTable
      minWidth={1180}
      rows={newestFirst(rows)}
      onRow={onOpen}
      columns={[
        { key: "name", title: t("orders_popup_order_id_title"), dataIndex: "name", width: 140, render: value => <b>{value}</b> },
        /* Widthed. It was the only column without one, so it took every spare pixel and left the
           gap the delivery date now fills. */
        { key: "cp", title: t("orders_popup_counterparty_label"), dataIndex: ["counterparty", "name"], width: 240, ellipsis: true },
        /* The counterparty's OWN address, not the visited point's — `Counterparty.address` was on the
           model all along but `CounterpartyShortSerializer` (the one every visit section nests) was
           the only Counterparty serializer not exposing it. */
        {
          key: "cp_address",
          title: t("orders_address_label"),
          dataIndex: ["counterparty", "address"],
          width: 220,
          ellipsis: true,
          render: value => value || dash,
        },
        /* Annotated server-side (`Count("items")`). The list payload carries no `items` — the modal
           fetches the detail for those — so counting client-side would have meant one request per
           row. */
        {
          key: "n",
          title: t("orders_items_count_label"),
          dataIndex: "items_count",
          width: 110,
          align: "right",
          render: value => <span className="vd-num">{value ?? dash}</span>,
        },
        {
          key: "delivery",
          title: t("orders_delivery_label"),
          dataIndex: "delivery_date",
          width: 140,
          render: value => (value ? formatDate(value, lang) : dash),
        },
        { key: "at", title: t("orders_product_status_date"), dataIndex: "datetime", width: 160, render: value => formatDateTime(value, lang) },
        { key: "status", title: t("status_label"), dataIndex: "status", width: 180, render: value => <OrderStatusTag value={value} lang={lang} /> },
        {
          key: "total",
          title: t("orders_product_total"),
          dataIndex: "order_total",
          width: 130,
          align: "right",
          render: value => <span className="vd-num">{money(value)}</span>,
        },
        openColumn(t("view_details")),
      ]}
    />
  );
}

export function ReturnsSection({ data, lang, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <SectionTable
      minWidth={1150}
      rows={newestFirst(rows)}
      onRow={onOpen}
      columns={[
        { key: "id", title: t("orders_return_id_label"), dataIndex: "name", width: 130, render: (value, row) => <b>{value || `#${row.id}`}</b> },
        { key: "cp", title: t("orders_popup_counterparty_label"), dataIndex: ["counterparty", "name"], width: 260, ellipsis: true },
        {
          key: "cp_address",
          title: t("orders_address_label"),
          dataIndex: ["counterparty", "address"],
          width: 220,
          ellipsis: true,
          render: value => value || dash,
        },
        /* Counted from the row, no backend change needed: `items` IS nested on the return LIST
           serializer (the detail modal reuses the row rather than fetching), with `items__product`
           prefetched. An order is the opposite case — see its column above. */
        {
          key: "n",
          title: t("orders_items_count_label"),
          width: 110,
          align: "right",
          render: (value, row) => <span className="vd-num">{(row.items || []).length}</span>,
        },
        { key: "at", title: t("orders_return_crated_label"), dataIndex: "datetime", width: 160, render: value => formatDateTime(value, lang) },
        {
          key: "phys",
          title: t("orders_physical_return_label"),
          dataIndex: "date_of_physical_return",
          width: 170,
          render: value => (value ? formatDate(value, lang) : dash),
        },
        /* No Status column: a return's document status is Excalibur's, it never enters the review
           queue, and the detail modal still shows it. A Total belongs here (PICSELL-1572) but the
           web API models neither a return total nor a per-item price — a backend item, not a
           column that could be filled with a wrong number today. */
        openColumn(t("view_details")),
      ]}
    />
  );
}

export function PaymentsSection({ data, lang, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <SectionTable
      minWidth={1040}
      rows={newestFirst(rows)}
      onRow={onOpen}
      columns={[
        { key: "id", title: t("orders_payment_id_label"), dataIndex: "id", width: 120, render: value => <b>#{value}</b> },
        { key: "cp", title: t("orders_popup_counterparty_label"), dataIndex: ["counterparty", "name"], width: 240, ellipsis: true },
        /* ⚠ `debt.name` does not exist — `Debt` is serialised as `(id, name_of_the_document)`, so
           this column was blank on every row and read as the empty gap QA reported. */
        { key: "debt", title: t("orders_payment_debt_label"), dataIndex: ["debt", "name_of_the_document"], width: 180, ellipsis: true },
        {
          key: "amount",
          title: t("orders_payment_amount_label"),
          dataIndex: "amount",
          width: 130,
          align: "right",
          render: value => <span className="vd-num">{money(value)}</span>,
        },
        { key: "at", title: t("orders_product_status_date"), dataIndex: "datetime", width: 160, render: value => formatDateTime(value, lang) },
        { key: "comment", title: t("orders_popup_comment_label"), dataIndex: "comment", ellipsis: true, render: value => value || dash },
        openColumn(t("view_details")),
      ]}
    />
  );
}

/* ------------------------------------------------------------------ reports */

/**
 * `photos_in_visit` proxies Outforz and joins picsell `Approval` rows onto the page it fetched —
 * the same objects and the same review state as /photo-review.
 *
 * ⚠ The rows go through the `PhotoReview` model, and that is not decoration. `photo` is NOT an API
 * field: the payload carries `url_photo`, or `ticket_contents[].url_photo` for a grouped ticket, and
 * `imgGroup` is the only thing that flattens either into the array both this grid and the review
 * modal read. Rendering the raw rows is why every cover here was a placeholder glyph. It is also
 * exactly the shape `ReviewModal` needs, so the same wrapping is what lets this page open the
 * report the same way /photo-review does instead of in a generic details dialog.
 */
export function ReportsSection({ data, lang, pager, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = useMemo(() => rowsOf(data).map(row => new PhotoReview(row)), [data]);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <Flex vertical gap="middle">
      <div className="vd-rgrid">
        {rows.map((item, index) => {
          const photo = item.photo?.[0];
          const title = item.additional_info?.title || item.ticket || `#${item.id}`;
          return (
            <Card
              key={item.id ?? index}
              className="vd-rcard"
              variant="outlined"
              hoverable
              /* The whole loaded page goes over, so the modal can step it. */
              onClick={() => onOpen(rows, index)}
              cover={
                <div className="vd-rcard__c">
                  {/* ⚠ `rootClassName` is load-bearing: antd wraps the `<img>` in a `span.ant-image`,
                      so sizing only the inner element leaves the wrapper at the photo's intrinsic
                      size and the cover box never applies. Same trap `review-modal.scss` records. */}
                  {photo ? (
                    <Image src={photo} alt="" preview={false} rootClassName="vd-rcard__imgwrap" />
                  ) : (
                    <FileImageOutlined className="vd-rcard__none-icon" />
                  )}
                  <StatusTag status={statusOf(item.approval)} lang={lang} className="vd-rcard__pill" />
                </div>
              }>
              <Typography.Text strong ellipsis={{ tooltip: title }} className="vd-rcard__t">
                {title}
              </Typography.Text>
              {/* Chips, not a dot-joined string: the same tags are chips in the review modal this
                  card opens into, and low-contrast run-on text was indistinguishable from the
                  caption above it. A single tag arrives as a bare string, not an array. */}
              <Space size={4} wrap className="vd-rcard__tags">
                {tagsOf(item.additional_info?.tags).map((tag, i) => (
                  <Tag key={`${tag}-${i}`} className="vd-rcard__tag">
                    {tag}
                  </Tag>
                ))}
              </Space>
            </Card>
          );
        })}
      </div>
      {/* Outforz pages this at 20 and the proxy forwards nothing but `page`, so there is no
          rows-per-page control to offer — and no total either, hence next/prev (`TableSteps`)
          rather than the numbered footer every other section uses. */}
      {pager ? (
        <TableSteps
          id="vdrep"
          page={pager.page}
          hasNext={Boolean(data?.next)}
          onPage={pager.onPage}
          labels={{ page: t("page_n"), prev: t("page_prev"), next: t("page_next") }}
        />
      ) : null}
    </Flex>
  );
}

/* ------------------------------------------------------------------ tasks */

const TASK_SIZES = [10, 25, 50];

/**
 * Tasks. Paged CLIENT-side, because the whole list is already here: `tasks_in_visit/` declares a
 * pagination class and then ignores it, serialising the visit's tasks into a bare array. So a
 * request per page would fetch the same everything again — the rows are sliced instead, and the
 * footer's total is the real one rather than a page's worth.
 *
 * Rows with an answer are selectable, and the bulk bar decides the whole selection in ONE
 * `approvals/bulk_approval/` request (PICSELL-1581 #1). Bulk reject takes one shared reason and
 * raises no corrective tasks — that flow stays per-record, in the queue and the record modal.
 */
export function TasksSection({ data, lang, onOpen, busy, onBulkDecide }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const all = rowsOf(data);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(TASK_SIZES[1]);
  const [selected, setSelected] = useState([]);
  const [rejecting, setRejecting] = useState(false);
  const rows = all.slice((page - 1) * size, page * size);
  if (!all.length) return <SectionEmpty text={t("nothing_here")} />;

  /* A verdict is posted against the ANSWER, so the selection resolves task rows → answer ids. */
  const answerIds = all.filter(row => selected.includes(row.id) && row.answer?.id).map(row => row.answer.id);

  const decide = (isApproved, comment = "") =>
    onBulkDecide(answerIds, isApproved, comment, ok => {
      if (!ok) return;
      setRejecting(false);
      setSelected([]);
    });

  return (
    <Flex vertical gap="middle">
      <SectionTable
        minWidth={1120}
        rows={rows}
        onRow={onOpen}
        rowSelection={
          onBulkDecide
            ? {
                selectedRowKeys: selected,
                onChange: keys => setSelected(keys),
                /* Only an answered task carries anything a verdict can land on. */
                getCheckboxProps: row => ({ disabled: !row.answer?.id }),
              }
            : undefined
        }
        columns={[
          {
            key: "status",
            title: t("task_tab_answer_status_label"),
            width: 150,
            /* `is_supervised`, the column every verdict is mirrored into — this serializer never
               carried `approval`, so bulk-approving left every row "on verification" (PICSELL-1615
               #1). A task nobody answered shows a dash, not a claim. */
            render: (value, row) => (row.answer ? <StatusTag status={verdictOf(row.answer)} lang={lang} /> : dash),
          },
          { key: "template", title: t("task_tab_type_label"), dataIndex: ["template", "name"], width: 170, ellipsis: true },
          {
            key: "title",
            title: t("task_tab_task_name_label"),
            dataIndex: "title",
            ellipsis: true,
            render: (value, row) => (
              <Flex vertical className="vd-cell">
                <Typography.Text ellipsis={{ tooltip: value }}>{value}</Typography.Text>
                <Typography.Text type="secondary" className="vd-cell__s" ellipsis={{ tooltip: row.description }}>
                  {row.description}
                </Typography.Text>
              </Flex>
            ),
          },
          { key: "end", title: t("task_tab_deadline_label"), dataIndex: "end", width: 150, render: value => (value ? formatDate(value, lang) : dash) },
          {
            key: "photos",
            title: t("task_tab_count_photo_label"),
            dataIndex: "image_count",
            width: 100,
            align: "center",
            render: value => (value ? <span className="vd-num">{value}</span> : dash),
          },
          {
            key: "answer",
            title: t("task_tab_answer_comment_label"),
            width: 200,
            ellipsis: true,
            render: (value, row) => row.answer?.answer || <span className="vd-dash">{t("no_answer")}</span>,
          },
          openColumn(t("view_details")),
        ]}
      />
      <SectionFoot
        id="vd-tasks"
        total={all.length}
        pager={{
          page,
          size,
          sizes: TASK_SIZES,
          labels: { perPage: t("rows_per_page"), range: t("range_of") },
          onPage: setPage,
          onSize: next => {
            setSize(next);
            setPage(1);
          },
        }}
      />

      {selected.length > 0 && onBulkDecide ? (
        <Flex className="lp-bulkbar" align="center" gap="middle" wrap>
          <Typography.Text className="lp-bulkbar__n">{fill(t("w_selected"), { n: answerIds.length })}</Typography.Text>
          <Space className="lp-bulkbar__acts" size={8}>
            <Button size="small" onClick={() => setSelected([])}>
              {t("clear_selection")}
            </Button>
            <Button size="small" danger icon={<CloseCircleOutlined />} disabled={busy || !answerIds.length} onClick={() => setRejecting(true)}>
              {t("reject_btn")}
            </Button>
            <Button size="small" icon={<CheckCircleOutlined />} loading={busy} disabled={!answerIds.length} onClick={() => decide(true)}>
              {t("approv_btn")}
            </Button>
          </Space>
        </Flex>
      ) : null}

      {/* One shared reason for the whole batch — the price-monitoring reject box, not the
          corrective-task modal, which is a per-record decision. */}
      <RejectModal
        open={rejecting}
        target={{ title: fill(t("w_selected"), { n: answerIds.length }) }}
        lang={lang}
        onCancel={() => setRejecting(false)}
        onConfirm={reason => decide(false, reason)}
      />
    </Flex>
  );
}

/* ------------------------------------------------------------- price monitoring */

const PRICE_SEGMENTS = [
  ["all", "f_all"],
  ["pending", "on_verification_single_visit_name"],
  ["approved", "approved_single_visit_name"],
  ["rejected", "no_approved_single_visit_name"],
];

export function PriceSection({ data, lang, pager, onOpen }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const all = rowsOf(data);
  const total = data?.count ?? all.length;
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");

  /* ⚠ The segment and the search filter the LOADED PAGE, not the section. This endpoint paginates
     (200 a page by default) and cannot be filtered server-side, which is the same constraint
     /photo-review's client-side status segment lives under. The footer below still describes the
     PAGE (`1–50 of 200`), so while a filter is on it counts more rows than the table shows. */
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return all.filter(row => {
      if (status !== "all" && statusOf(row.approval) !== status) return false;
      if (!needle) return true;
      return `${row.product_name || ""} ${row.product_code || ""}`.toLowerCase().includes(needle);
    });
  }, [all, status, query]);

  return (
    <Flex vertical gap="middle">
      <Flex className="vd-toolbar" align="center" gap="small" wrap>
        <Segmented value={status} onChange={setStatus} options={PRICE_SEGMENTS.map(([value, key]) => ({ value, label: t(key) }))} />
        <Input
          className="vd-toolbar__search"
          value={query}
          allowClear
          prefix={<SearchOutlined />}
          aria-label={t("monitoring_tab_pname_label")}
          onChange={event => setQuery(event.target.value)}
        />
      </Flex>

      {rows.length ? (
        <SectionTable
          minWidth={900}
          rows={rows}
          onRow={onOpen}
          columns={[
            {
              key: "photo",
              title: "",
              width: 66,
              render: (value, row) => (row.photo_url ? <Image src={row.photo_url} alt="" width={46} height={34} className="vd-thumb" preview={false} /> : dash),
            },
            {
              key: "product",
              title: t("monitoring_tab_pname_label"),
              ellipsis: true,
              render: (value, row) => (
                <Flex vertical className="vd-cell">
                  <Typography.Text ellipsis={{ tooltip: row.product_name }}>{row.product_name}</Typography.Text>
                  <Typography.Text type="secondary" className="vd-cell__s">
                    {row.product_code}
                  </Typography.Text>
                </Flex>
              ),
            },
            {
              key: "price",
              title: t("monitoring_tab_price_label"),
              dataIndex: "price",
              width: 120,
              align: "right",
              render: value => <span className="vd-num">{money(value)}</span>,
            },
            {
              key: "sale",
              title: t("monitoring_tab_sale_price_label"),
              dataIndex: "sale_price",
              width: 130,
              align: "right",
              render: value => <span className="vd-num">{money(value)}</span>,
            },
            {
              key: "delta",
              title: t("delta"),
              width: 100,
              align: "right",
              render: (value, row) => <Delta price={row.price} salePrice={row.sale_price} />,
            },
            { key: "status", title: t("status_label"), width: 150, render: (value, row) => <StatusTag status={statusOf(row.approval)} lang={lang} /> },
            openColumn(t("view_details")),
          ]}
        />
      ) : (
        <SectionEmpty text={t("nothing_here")} />
      )}
      {/* The pager is the section's, so it stays put while the client-side filter above narrows
          what is drawn from the page it fetched. */}
      <SectionFoot id="vd-price" total={total} pager={pager} />
    </Flex>
  );
}

/** Sale price against list price. A drop is good news for the shopper, so it is the success tone;
    the arithmetic is only shown when both numbers exist. */
export function Delta({ price, salePrice }) {
  if (price === null || price === undefined || salePrice === null || salePrice === undefined) return dash;
  const value = Number(salePrice) - Number(price);
  if (!value) return dash;
  return <span className={`vd-num vd-delta vd-delta--${value < 0 ? "down" : "up"}`}>{`${value > 0 ? "+" : ""}${value.toFixed(2)}`}</span>;
}

/* ------------------------------------------------------------------ surveys */

/**
 * Surveys: an accordion, one survey expanded at a time, each loading its own answers.
 *
 * The legacy tab rendered every survey fully expanded with all four group tabs mounted — with 14
 * surveys × up to 12 entities × up to 6 questions that is thousands of rows in the DOM. Here the
 * list costs one request and each survey costs one more, only when opened.
 */
export function SurveysSection({ visitId, data, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  /* Only surveys with something recorded. `surveys_in_visit` lists every survey TARGETING the
     point, answered or not, and an untouched one is noise in a review queue — the legacy tab
     filtered the same way. The summary counts above still describe all of them. */
  const surveys = (data?.surveys || []).filter(survey => (survey.answers_count ?? 0) > 0 || (survey.photos_count ?? 0) > 0);
  const summary = data?.summary || {};
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState({});
  const [loading, setLoading] = useState(null);

  const toggle = survey => {
    if (open === survey.id) {
      setOpen(null);
      return;
    }
    setOpen(survey.id);
    if (detail[survey.id]) return;
    setLoading(survey.id);
    call({ method: "GET", url: `visit_surveys/${survey.id}/?visit_id=${visitId}` }, response => {
      setLoading(null);
      setDetail(prev => ({ ...prev, [survey.id]: response || {} }));
    });
  };

  if (!surveys.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <Flex vertical gap="middle">
      <Flex className="vd-svstats" gap="middle" wrap>
        {[
          [summary.surveys, t("surveys_summary_surveys_label")],
          [summary.answers, t("surveys_summary_answers_label")],
          [summary.photos, t("surveys_summary_photos_label")],
        ].map(([value, label]) => (
          <Flex vertical key={label} className="vd-svstat">
            <span className="vd-svstat__n">{value ?? 0}</span>
            <Typography.Text type="secondary" className="vd-svstat__l">
              {label}
            </Typography.Text>
          </Flex>
        ))}
      </Flex>

      <Flex vertical gap="small">
        {surveys.map(survey => (
          <SurveyCard
            key={survey.id}
            survey={survey}
            lang={lang}
            open={open === survey.id}
            loading={loading === survey.id}
            detail={detail[survey.id]}
            onToggle={() => toggle(survey)}
          />
        ))}
      </Flex>
    </Flex>
  );
}

/* `visit_surveys/:id/` groups the answers four ways, and the fourth is NOT shaped like the others:
   products / brands / categories are blocks of `{ <entity>: {...}, answers: [...] }`, while `other`
   is a FLAT list of answers with no entity to hang them under. The entity's name is nested under
   its own key — reading `entity.name` (as this did) is undefined for all three, which is why every
   header rendered blank. */
const GROUPS = [
  ["products", "surveys_tab_products", "product"],
  ["brands", "surveys_tab_brands", "brand"],
  ["categories", "surveys_tab_categories", "category"],
  ["other", "surveys_tab_other", null],
];

function SurveyCard({ survey, lang, open, loading, detail, onToggle }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const [group, setGroup] = useState("products");
  const groups = detail?.groups || {};
  const entities = groups[group] || [];
  const entityKey = GROUPS.find(([key]) => key === group)?.[2];

  return (
    <Card className={`vd-sv${open ? " vd-sv--open" : ""}`} variant="outlined" size="small">
      <button type="button" className="vd-sv__head" aria-expanded={open} onClick={onToggle}>
        <Typography.Text strong className="vd-sv__t">
          {survey.title || survey.name}
        </Typography.Text>
        <Typography.Text type="secondary" className="vd-sv__m">
          {survey.answers_count ?? 0} · {t("surveys_summary_answers_label")}
        </Typography.Text>
      </button>

      {open ? (
        <div className="vd-sv__body">
          <Segmented value={group} onChange={setGroup} options={GROUPS.map(([key, labelKey]) => ({ value: key, label: t(labelKey) }))} />
          {loading ? (
            <Typography.Text type="secondary">{translate(ENTITY_DROPDOWN_SETTINGS, "option_loading", lang)}</Typography.Text>
          ) : !entities.length ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("surveys_no_results")} />
          ) : entityKey ? (
            <Flex vertical gap="small" className="vd-sv__ents">
              {entities.map((entity, index) => (
                <SurveyEntity key={entity[entityKey]?.id ?? index} entity={entity} entityKey={entityKey} lang={lang} />
              ))}
            </Flex>
          ) : (
            /* `other` is already the answers — there is no entity to collapse it under. */
            <AnswerRows answers={entities} lang={lang} />
          )}
        </div>
      ) : null}
    </Card>
  );
}

function SurveyEntity({ entity, entityKey, lang }) {
  const [open, setOpen] = useState(false);
  const answers = entity.answers || [];
  const subject = entity[entityKey] || {};

  return (
    <div className={`vd-ent${open ? " vd-ent--open" : ""}`}>
      <button type="button" className="vd-ent__h" aria-expanded={open} onClick={() => setOpen(value => !value)}>
        <Typography.Text strong>{subject.name || dash}</Typography.Text>
        {subject.code ? (
          <Typography.Text type="secondary" className="vd-ent__c">
            {subject.code}
          </Typography.Text>
        ) : null}
        <span className="vd-spacer" />
        <Typography.Text type="secondary">{answers.length}</Typography.Text>
      </button>
      {open ? <AnswerRows answers={answers} lang={lang} /> : null}
    </div>
  );
}

/* Answers arrive in insertion order, which is the order they were ANSWERED — the questionnaire's own
   order is `question.order`, and that is the order a reviewer reads the survey in. Sorted here, in
   the one place both the per-entity groups and the flat `other` group render through, rather than
   per group at fetch time. */
function AnswerRows({ answers, lang }) {
  const ordered = useMemo(() => [...answers].sort((a, b) => (a.question?.order ?? 0) - (b.question?.order ?? 0)), [answers]);

  return (
    <div className="vd-qa">
      {ordered.map((answer, index) => (
        <div className="vd-qa__row" key={answer.id ?? index}>
          {/* `question` is an OBJECT here. Rendering it directly threw "Objects are not valid as a
              React child", which took the whole section down the moment an entity was expanded. */}
          <Typography.Text className="vd-qa__q">
            {answer.question?.required ? <Tag>{translate(POINTS_ADMIN, "f_required", lang)}</Tag> : null}
            {answer.question?.text || dash}
          </Typography.Text>
          <span className="vd-qa__a">
            <AnswerValue answer={answer} lang={lang} />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * One answer's value.
 *
 * A survey answer is stored in a column PER TYPE (`answer_text`, `answer_numeric`,
 * `answer_dropdown`, `answer_date`, `answer_boolean`, `photos`) and `question.answer_type` says
 * which one holds it — there is no generic `answer` / `value` field, which is why every non-photo
 * answer used to render as a dash.
 */
function AnswerValue({ answer, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const photos = (answer.photos || []).map(photo => photo.photo).filter(Boolean);

  switch (answer.question?.answer_type) {
    case "boolean":
      /* Tri-state: null is "not answered", which is not the same statement as "no".
         Plain text, not a status tag — the legacy tab painted "No" red, but a negative survey
         answer is data and may well be the expected one; only an approval verdict earns a tone. */
      if (answer.answer_boolean === null || answer.answer_boolean === undefined) return dash;
      return t(answer.answer_boolean ? "surveys_answer_bool_yes" : "surveys_answer_bool_no");
    case "numeric":
      return answer.answer_numeric === null || answer.answer_numeric === undefined ? dash : <span className="vd-num">{answer.answer_numeric}</span>;
    case "dropdown":
      return answer.answer_dropdown?.label || dash;
    case "text":
      return answer.answer_text || dash;
    case "date":
      return answer.answer_date ? formatDate(answer.answer_date, lang) : dash;
    case "photo":
      if (!photos.length) return dash;
      /* antd's own lightbox, with arrows and a counter — the same thing /answers and /task use
         rather than a hand-rolled overlay. */
      return (
        <Image.PreviewGroup>
          <Flex gap={4} wrap>
            {photos.map((src, index) => (
              <Image key={index} src={src} alt="" width={64} className="vd-thumb" />
            ))}
          </Flex>
        </Image.PreviewGroup>
      );
    default:
      /* An unknown type is still worth showing whatever was recorded rather than nothing. */
      return answer.answer_text || answer.answer_dropdown?.label || dash;
  }
}

/* ------------------------------------------------------------------ GPS + log */

/** The recorded track lives on the visit itself (`visit.meta`), so this section costs no request.
    Every ping is drawn as a dot on the polyline (the mockup's shape), the visited point as the one
    full-size pin; a mock-flagged ping turns its dot red and raises the warning banner. */
export function GpsSection({ visit, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const pings = useMemo(() => pingsOf(visit), [visit]);
  const track = useMemo(() => pings.map(ping => ping.pos), [pings]);
  const point = visit?.point?.location;
  const pointPos = point ? [Number(point.latitude), Number(point.longitude)] : null;
  const mockCount = pings.filter(isMockPing).length;
  /* The chosen ping, by index — from the numbered list or from its dot on the map. */
  const [selected, setSelected] = useState(null);
  const pick = index => setSelected(current => (current === index ? null : index));

  const markers = useMemo(() => {
    const out = pings.map((ping, index) => ({
      key: `ping-${index}`,
      index,
      lat: ping.pos[0],
      lng: ping.pos[1],
      /* The chosen ping is the full-size numbered pin; every other one stays a dot. */
      kind: index === selected ? "sel" : "dot",
      label: index === selected ? index + 1 : undefined,
      tone: isMockPing(ping) ? "no" : undefined,
      title: `${index + 1}${ping.time ? ` · ${formatTime(ping.time, lang)}` : ""}`,
      clickable: true,
    }));
    /* ⚠ Passing `markers` suppresses LeafletMap's own primary pin, so the point rides along in
       the same array — which is also what lets `fit()` frame the point AND the whole track. */
    if (point) out.push({ key: "point", lat: point.latitude, lng: point.longitude, kind: "point", title: visit?.point?.name });
    return out;
  }, [pings, point, visit, lang, selected]);

  /* Frame the chosen ping WITH the point, so "how far off was it" is answered by the map itself. */
  const focus = selected == null || !pings[selected] ? null : [pings[selected].pos, ...(pointPos ? [pointPos] : [])];

  const units = { m: t("gps_m"), km: t("gps_km") };
  const stats = [
    [t("point_started_title"), formatTime(visit?.start, lang)],
    [t("visit_end"), formatTime(visit?.end, lang)],
    [t("point"), visit?.point?.name],
  ];

  return (
    <Flex vertical gap="middle">
      {mockCount ? <AntAlert type="warning" showIcon message={fill(t("gps_mock_warning"), { n: mockCount })} /> : null}
      <div className="vd-gps">
        <div>
          {point || track.length ? (
            <LeafletMap
              lat={point?.latitude}
              lng={point?.longitude}
              markers={markers}
              track={track}
              focus={focus}
              onMarkerClick={marker => marker.index != null && pick(marker.index)}
              height={320}
            />
          ) : (
            <SectionEmpty text={t("no_coordinates")} />
          )}
        </div>
        <Flex vertical gap="small" className="vd-gps__stats">
          {stats.map(([label, value]) => (
            <Flex key={label} align="center" justify="space-between" gap="small" className="vd-gps__stat">
              <Typography.Text type="secondary">{label}</Typography.Text>
              <Typography.Text strong>{value || dash}</Typography.Text>
            </Flex>
          ))}

          {/* The track as a numbered list — the number is what ties a row to its pin. Real
              <button>s (spec/03), `aria-pressed` for the chosen one. */}
          <Typography.Text type="secondary" className="vd-gps__head">
            {t("gps_points")}
          </Typography.Text>
          {pings.length ? (
            <>
              <Typography.Text type="secondary" className="vd-gps__hint">
                {t("gps_from_point")}
              </Typography.Text>
              <div className="vd-gps__list">
                {pings.map((ping, index) => {
                  const on = index === selected;
                  const mock = isMockPing(ping);
                  return (
                    <button type="button" key={index} className={`vd-ping${on ? " vd-ping--on" : ""}`} aria-pressed={on} onClick={() => pick(index)}>
                      <span className={`vd-ping__n${mock ? " vd-ping__n--no" : ""}`}>{index + 1}</span>
                      <span className="vd-ping__time">{ping.time ? formatTime(ping.time, lang) : dash}</span>
                      <span className="vd-ping__dist">{pointPos ? formatDistance(distanceM(ping.pos, pointPos), units, localeFor(lang)) : dash}</span>
                      {mock ? <Tag color="error">{t("gps_mock_tag")}</Tag> : null}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <Typography.Text type="secondary" className="vd-gps__hint">
              {dash}
            </Typography.Text>
          )}
        </Flex>
      </div>
    </Flex>
  );
}

export function LogSection({ data, lang }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  const rows = rowsOf(data);
  if (!rows.length) return <SectionEmpty text={t("nothing_here")} />;

  return (
    <Timeline
      className="vd-log"
      items={rows.map(entry => ({
        key: entry.id,
        children: (
          <Flex vertical gap={2}>
            <Typography.Text>{entry.additional_info?.event || entry.action}</Typography.Text>
            <Flex align="center" gap="small">
              <Typography.Text type="secondary" className="vd-log__m">
                {formatDateTime(entry.timestamp, lang)}
              </Typography.Text>
              {entry.user ? (
                /* The same avatar as every other person in the app: initials, photo preferred.
                   28px rather than the log's old 20 because the initials are the 12px scale
                   floor and two characters at 12px do not fit a smaller circle (spec/01#type). */
                <PersonCell user={entry.user} strong={false} />
              ) : null}
            </Flex>
          </Flex>
        ),
      }))}
    />
  );
}

/* ------------------------------------------------------------------ dates */

/* Intl has no hy-AM / ka-GE data in some Chrome builds and silently resolves to en-US; `localeFor`
   is the app's mapping and en-GB is the day-first fallback the rest of the redesign uses. */
import { localeFor } from "../merchandiser/route/dateUtils";

const fmt = (value, lang, options) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(localeFor(lang), options).format(date);
};

export const formatDate = (value, lang) => fmt(value, lang, { day: "2-digit", month: "2-digit", year: "numeric" });
export const formatDateTime = (value, lang) => fmt(value, lang, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
export const formatTime = (value, lang) => fmt(value, lang, { hour: "2-digit", minute: "2-digit" });

/* Kept beside the sections that render them: the approve/reject pair the queue rows carry, so a
   verdict always reads the same wherever it is offered.

   It owns its own <Flex> rather than returning a Fragment. A Fragment looks equivalent and is not:
   antd's <Space> splits its own DIRECT children into `.ant-space-item`s, and a function-component
   element is ONE child however many nodes it returns — so both buttons landed in a single item with
   the gap outside them, touching, and the ≤620px `.ant-btn { flex: 1 }` rule had a non-flex parent
   to stretch against. */
export function VerdictButtons({ lang, size = "small", className = "", onApprove, onReject, busy }) {
  const t = key => translate(VISIT_PAGE, key, lang);
  return (
    <Flex className={className} align="center" gap={8}>
      <Button size={size} danger icon={<CloseCircleOutlined />} loading={busy} onClick={onReject}>
        {t("reject_btn")}
      </Button>
      <Button size={size} className="vd-btn--ok" icon={<CheckCircleOutlined />} loading={busy} onClick={onApprove}>
        {t("approv_btn")}
      </Button>
    </Flex>
  );
}
